import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { getGiftConfig, updateGiftConfig, getProductPricing } from '../config/api';

// Marketing config: pick the complimentary gift product from a dropdown (the
// SKU is derived from the selection). The gift is auto-added to carts that have
// at least one real product, and only while it is active + in stock in a hub.
const selectStyles = {
  container: (base) => ({ ...base, maxWidth: 420 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

export default function GiftConfig() {
  const [config, setConfig] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedSku, setSelectedSku] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('userData_30jan') || '{}');
    if (userData.role !== 'superAdmin' && !userData.access?.includes('marketing')) {
      window.location.href = '/orders';
      return;
    }
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [cfgRes, prodRes] = await Promise.all([getGiftConfig(), getProductPricing(1)]);
      const cfg = cfgRes?.data || null;
      setConfig(cfg);
      setSelectedSku(cfg?.sku || '');
      setProducts(prodRes?.data || []);
    } catch (e) {
      toast.error(e?.message || 'Failed to load gift config');
    } finally {
      setLoading(false);
    }
  };

  const options = useMemo(
    () =>
      (products || [])
        .filter((p) => p.skuId)
        .map((p) => ({
          value: p.skuId,
          label: `${p.productName} — ${p.skuId}${p.brand ? ` (${p.brand})` : ''}`,
        })),
    [products]
  );

  // Keep the currently-configured SKU selectable even if it's not in the list
  // (e.g. product inactive in hub 1), so the user always sees what's set.
  const selectedOption = useMemo(() => {
    if (!selectedSku) return null;
    return (
      options.find((o) => o.value === selectedSku) || {
        value: selectedSku,
        label: config?.productName ? `${config.productName} — ${selectedSku}` : selectedSku,
      }
    );
  }, [options, selectedSku, config]);

  const handleSave = async () => {
    if (!selectedSku) {
      toast.error('Please select a gift product');
      return;
    }
    setSaving(true);
    try {
      const res = await updateGiftConfig(selectedSku);
      setConfig(res?.data || null);
      toast.success(res?.message || 'Gift product updated');
    } catch (e) {
      toast.error(e?.message || 'Failed to update gift config');
    } finally {
      setSaving(false);
    }
  };

  const availableHubs = (config?.warehouses || []).filter((w) => w.availableToCustomers);

  return (
    <div className="container">
      <div className="card">
        <div className="order-detail-header" style={{ marginBottom: 12 }}>
          <div className="order-detail-title">
            <h1 style={{ marginBottom: 4 }}>Gift Product</h1>
            <p style={{ color: '#6c757d', margin: 0, fontSize: 13, maxWidth: 620 }}>
              Pick the complimentary gift. It's added automatically when a cart has a
              product, and only while the gift is active and in stock in that hub.
            </p>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 32, textAlign: 'center', color: '#6c757d' }}>Loading…</div>
        ) : (
          <>
            {/* Compact picker row: dropdown + save, side by side */}
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 16 }}>
              <div style={{ flex: '1 1 320px', minWidth: 260 }}>
                <label className="info-label" style={{ display: 'block', marginBottom: 6 }}>Gift product</label>
                <Select
                  value={selectedOption}
                  onChange={(opt) => setSelectedSku(opt ? opt.value : '')}
                  options={options}
                  styles={selectStyles}
                  placeholder="Search and select a product…"
                  isSearchable
                  isClearable
                  noOptionsMessage={() => 'No products found'}
                  classNamePrefix="react-select"
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                />
              </div>
              <button className="btn btn-success" onClick={handleSave} disabled={saving || !selectedSku}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>

            {/* Status — compact */}
            {config?.productFound ? (
              <div style={{ borderTop: '1px solid #e9ecef', paddingTop: 14 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, marginBottom: 12, fontSize: 13 }}>
                  <span><span style={{ color: '#6c757d' }}>Product:</span> <strong>{config.productName}</strong></span>
                  <span><span style={{ color: '#6c757d' }}>SKU:</span> <strong>{config.sku}</strong></span>
                  <span>
                    <span style={{ color: '#6c757d' }}>Live in hubs:</span>{' '}
                    <strong style={{ color: availableHubs.length ? '#16a34a' : '#dc3545' }}>
                      {availableHubs.length}/{config.warehouses.length}
                    </strong>
                  </span>
                </div>

                {availableHubs.length === 0 && (
                  <div style={{ padding: '8px 10px', background: '#fff4e5', border: '1px solid #ffd8a8', borderRadius: 6, color: '#9a5b00', fontSize: 12, marginBottom: 12 }}>
                    Not active + in stock in any hub yet, so the gift won't show to customers. Activate its warehouse stock and add inventory.
                  </div>
                )}

                <details>
                  <summary style={{ cursor: 'pointer', fontSize: 13, color: '#374151' }}>Per-hub availability</summary>
                  <div className="order-items-table" style={{ marginTop: 8 }}>
                    <table>
                      <thead>
                        <tr>
                          <th>Hub</th>
                          <th>Active</th>
                          <th>Inventory</th>
                          <th style={{ textAlign: 'right' }}>Shows</th>
                        </tr>
                      </thead>
                      <tbody>
                        {config.warehouses.map((w) => (
                          <tr key={w.warehouseId}>
                            <td style={{ fontWeight: 600 }}>{w.warehouseName || `Hub ${w.warehouseId}`}</td>
                            <td>{w.active ? 'Yes' : 'No'}</td>
                            <td>{w.inventoryCount}</td>
                            <td style={{ textAlign: 'right' }}>
                              <span style={{
                                fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
                                background: w.availableToCustomers ? '#dcfce7' : '#fee2e2',
                                color: w.availableToCustomers ? '#166534' : '#991b1b',
                              }}>
                                {w.availableToCustomers ? 'Yes' : 'No'}
                              </span>
                            </td>
                          </tr>
                        ))}
                        {config.warehouses.length === 0 && (
                          <tr><td colSpan={4} style={{ color: '#6c757d', textAlign: 'center' }}>No warehouse rows.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </details>
              </div>
            ) : (
              config?.sku && (
                <div style={{ borderTop: '1px solid #e9ecef', paddingTop: 14 }}>
                  <div style={{ padding: 10, background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 6, color: '#991b1b', fontSize: 12 }}>
                    No product found for the saved SKU "<strong>{config.sku}</strong>". Pick an existing product above.
                  </div>
                </div>
              )
            )}
          </>
        )}
      </div>
    </div>
  );
}
